import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { cn } from "../private/utils.js";

const DrawerRoot = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
);
DrawerRoot.displayName = "DrawerRoot";

const DrawerTrigger = DrawerPrimitive.Trigger;
const NestedRoot = DrawerPrimitive.NestedRoot;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) => (
  <DrawerPrimitive.Overlay
    className={cn(
      "bg-mono-100/50 backdrop-blur-xs  fixed inset-0 z-50 dark:bg-black/50",
      className
    )}
    {...props}
  />
);

DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = ({
  className,
  children,
  innerClassName,
  ref,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  slideoutContainerId?: string;
  innerClassName?: string;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      className={cn(
        "outline-none border-border bg-background fixed inset-x-0 bottom-0 z-50 mx-0.5 mt-24 flex h-auto max-h-[95dvh] min-h-[25dvh] flex-col rounded-t-[10px] border shadow-[0px_-1px_25px_1px_#e6e6e6] dark:shadow-none",
        className
      )}
      {...props}
    >
      <div
        aria-hidden
        className={cn(
          "mx-auto mt-3 h-1.5 w-14 flex-none rounded-full",
          "bg-gradient-to-b from-mono-300 to-mono-500",
          "dark:from-mono-600 dark:to-mono-800",
          "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.5),0_1px_2px_0_rgba(0,0,0,0.18)]",
          "dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15),0_1px_2px_0_rgba(0,0,0,0.4)]"
        )}
      />
      <div
        ref={ref}
        className={cn(`mx-auto w-full overflow-y-auto`, innerClassName)}
      >
        {children}
      </div>
    </DrawerPrimitive.Content>
  </DrawerPortal>
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) => (
  <DrawerPrimitive.Title
    className={cn(
      "text-foreground pb-4 pt-2 text-lg  font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
);

DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

const Drawer = {
  Root: DrawerRoot,
  Portal: DrawerPortal,
  Overlay: DrawerOverlay,
  Trigger: DrawerTrigger,
  Close: DrawerClose,
  Content: DrawerContent,
  Header: DrawerHeader,
  Footer: DrawerFooter,
  Title: DrawerTitle,
  NestedRoot,
  Description: DrawerDescription
};

export { Drawer };
